import { motion, PanInfo, useMotionValue, useSpring } from "framer-motion";
import { MutableRefObject, useRef } from "react";
import { createBreakpoint, useWindowSize } from "react-use";
import styled, { css } from "styled-components";
import { sceneConfig } from "../../data/scene-config";
import { easings, fill } from "../../styles/utils";
import { CircularMotion } from "../CircularMotion";

import BGImage from "../../assets/img/home_scene.jpg";

interface SceneBackgroundProps {
  zoomOut: boolean;
  isAnimating: boolean;
  radius: number;
}

const { width, height } = sceneConfig;

const imageW = width;
const imageH = height;

const BackgroundWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 400%;

  ${({ theme }) => css`
    ${theme.mediaQueries.md} {
      width: 200%;
    }
    ${theme.mediaQueries.lg} {
      width: 150%;
    }
  `}
`;

const Wrapper = styled.div`
  position: relative;
  user-select: none;
  touch-action: none;
  cursor: none;
  width: 100%;
  padding-top: ${(height / width) * 100}%;
`;

const WrapperInner = styled.div`
  ${fill()}
`;

const ImageContainerOuter = styled(motion.div)`
  height: 100%;
  position: relative;
`;

const ZoomIn = styled(motion.div)`
  height: 100%;
  position: relative;
`;

const StyledImage = styled.img`
  ${fill()}
  pointer-events: none;
`;

const bMap = {
  xs: 4,
  sm: 4,
  md: 2,
  lg: 1.5,
};

const useBreakpoint = createBreakpoint({ xs: 0, sm: 576, md: 768, lg: 992 });
const r = height / width;

const zoomInVariants = {
  zoomOut: { scale: 0.85 },
  default: { scale: 1 },
};

export const SceneBackground: React.FC<SceneBackgroundProps> = ({
  zoomOut,
  isAnimating,
  children,
  radius,
}) => {
  const breakpoint = useBreakpoint() as keyof typeof bMap;
  const wrapperRef: MutableRefObject<any> = useRef<HTMLElement>();

  const { width, height } = useWindowSize();

  const xStop = useRef(0);
  const yStop = useRef(0);

  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const xLimit = (width / 2) * r;
  const yLimit = height / 2;

  const handlePan = (
    _: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo
  ) => {
    const xDrag = info.offset.x + xStop.current;
    const yDrag = info.offset.y + yStop.current;
    const xMove = calcMove(xDrag, (limitF() - xLimit) / r);
    const yMove = calcMove(yDrag, limitF() - yLimit);

    x.set(xMove);
    y.set(yMove);
  };

  const handlePanEnd = (
    e: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo
  ) => {
    const xDrag = info.offset.x + xStop.current;
    const yDrag = info.offset.y + yStop.current;
    const xMove = calcMove(xDrag, (limitF() - xLimit) / r);
    const yMove = calcMove(yDrag, limitF() - yLimit);

    xStop.current = xMove;
    yStop.current = yMove;
  };

  const limitF = () => {
    return (bMap[breakpoint] * width * r) / 2;
  };

  const calcMove = (v: number, limit: number): number => {
    const adjustedLimit = limit - radius - 1;
    if (v > 0) {
      return v < adjustedLimit ? v : adjustedLimit;
    }
    return v > -adjustedLimit ? v : -adjustedLimit;
  };

  const physics = { stiffness: 80, mass: 1, damping: 20 };
  const xMov = useSpring(x, physics);
  const yMov = useSpring(y, physics);

  return (
    <ZoomIn
      initial={false}
      animate={zoomOut ? "zoomOut" : "default"}
      variants={zoomInVariants}
      transition={{ duration: 3, ease: easings.easeInOutCubic }}
    >
      <BackgroundWrapper>
        <CircularMotion radius={radius} isAnimating={isAnimating}>
          <Wrapper ref={wrapperRef}>
            <WrapperInner>
              <ImageContainerOuter
                onPan={handlePan}
                onPanEnd={handlePanEnd}
                style={{ x: xMov, y: yMov }}
              >
                <StyledImage src={BGImage} alt="" />
                {children}
              </ImageContainerOuter>
            </WrapperInner>
          </Wrapper>
        </CircularMotion>
      </BackgroundWrapper>
    </ZoomIn>
  );
};
